import { useState, useEffect } from 'react';
import type { PviGraphWithSpans } from '../../editor/pviTypes';
import { useSimulationStore } from '../../stores/useSimulationStore';
import { SimulationControls } from './SimulationControls';
import { BalanceView } from './BalanceView';
import { TimelineView } from './TimelineView';
import {
  PREBUILT_MARKET_SCENARIOS,
  runMarketSimulationScenario,
  type MarketSimulationReport,
} from '../../editor/marketSimulation';
import styles from './Simulation.module.css';

interface SimulationPanelProps {
  graph: PviGraphWithSpans;
}

type SimTab = 'balances' | 'timeline' | 'market';

export function SimulationPanel({ graph }: SimulationPanelProps) {
  const [activeTab, setActiveTab] = useState<SimTab>('balances');
  const [selectedScenario, setSelectedScenario] = useState(PREBUILT_MARKET_SCENARIOS[0]?.name ?? '');
  const [marketReport, setMarketReport] = useState<MarketSimulationReport | null>(null);
  const result = useSimulationStore(s => s.result);
  const initFromGraph = useSimulationStore(s => s.initFromGraph);

  const chosenScenario = PREBUILT_MARKET_SCENARIOS.find(s => s.name === selectedScenario) ?? PREBUILT_MARKET_SCENARIOS[0];

  // Re-init inputs when graph changes
  useEffect(() => {
    initFromGraph(graph);
  }, [graph, initFromGraph]);

  return (
    <div className={styles.simPanel} data-testid="simulation-panel">
      {/* Graph area is handled by parent — this panel goes below */}

      {/* Result banner */}
      {result && result.steps.length > 0 && result.steps[result.steps.length - 1].status !== 'error' && (
        <div className={`${styles.resultBanner} ${result.success ? styles.resultSuccess : styles.resultFailed}`}>
          <span>{result.success ? '✔ Simulation Successful' : '✖ Simulation Failed'}</span>
          <span className={styles.resultMeta}>
            {result.steps.length} steps · {result.totalValueMoved.toLocaleString()} value moved
          </span>
        </div>
      )}

      {result && !result.success && result.errors.length > 0 && (
        <div className={styles.errorDetail}>
          <strong>Simulation Error</strong>
          {result.errors.map((e, i) => <div key={i}>{e}</div>)}
        </div>
      )}

      {/* Controls + inputs */}
      <SimulationControls />

      {/* Tab bar */}
      <div className={styles.simTabs}>
        <button
          className={`${styles.simTab} ${activeTab === 'balances' ? styles.simTabActive : ''}`}
          onClick={() => setActiveTab('balances')}
        >
          Balances
        </button>
        <button
          className={`${styles.simTab} ${activeTab === 'timeline' ? styles.simTabActive : ''}`}
          onClick={() => setActiveTab('timeline')}
        >
          Timeline
        </button>
        <button
          className={`${styles.simTab} ${activeTab === 'market' ? styles.simTabActive : ''}`}
          onClick={() => {
            setActiveTab('market');
            const report = runMarketSimulationScenario(chosenScenario, result ?? undefined);
            setMarketReport(report);
          }}
        >
          Market Harness
        </button>
      </div>

      {/* Tab content */}
      <div className={styles.simTabContent}>
        {activeTab === 'balances' && <BalanceView />}
        {activeTab === 'timeline' && <TimelineView />}
        {activeTab === 'market' && marketReport && (
          <div>
            <div className={styles.controlsRow}>
              <label htmlFor="scenario-select">Scenario</label>
              <select
                id="scenario-select"
                value={selectedScenario}
                onChange={(e) => {
                  const next = e.target.value;
                  setSelectedScenario(next);
                  const nextScenario = PREBUILT_MARKET_SCENARIOS.find(s => s.name === next) ?? PREBUILT_MARKET_SCENARIOS[0];
                  setMarketReport(runMarketSimulationScenario(nextScenario, result ?? undefined));
                }}
              >
                {PREBUILT_MARKET_SCENARIOS.map((scenario) => (
                  <option key={scenario.name} value={scenario.name}>
                    {scenario.name}
                  </option>
                ))}
              </select>
            </div>

            <div className={styles.resultBanner}>
              <span>Deterministic Scenario: {marketReport.scenario}</span>
              <span className={styles.resultMeta}>
                hash={marketReport.deterministicHash} · total fee={marketReport.totalFeeMicros.toLocaleString()} micros · worst-step fee={marketReport.worstCaseFeeMicros.toLocaleString()} micros
              </span>
            </div>

            <div className={styles.timelineStep}>
              <div className={styles.timelineStepHeader}>
                <span>Venue Policy</span>
                <span>VISIBLE</span>
              </div>
              <div>
                tick={marketReport.venuePolicy.tickSize} lot={marketReport.venuePolicy.lotSize} margin={marketReport.venuePolicy.marginMode}
              </div>
              <div>
                oracle_guard: max_age={marketReport.venuePolicy.oracleGuard.maxOracleAgeMs}ms deviation={marketReport.venuePolicy.oracleGuard.maxDeviationBps}bps breaker={marketReport.venuePolicy.oracleGuard.breakerTripBps}bps
              </div>
            </div>

            {marketReport.steps.map((step) => (
              <div key={step.index} className={styles.timelineStep}>
                <div className={styles.timelineStepHeader}>
                  <span>#{step.index} {step.kind}</span>
                  <span>{step.status.toUpperCase()}</span>
                </div>
                <div>{step.message} (reason={step.reasonCode})</div>
                <div>fee: {step.feeMicros.toLocaleString()} micros</div>
                <div>
                  fee-envelope: min={step.feeEnvelope.minFeeMicros} expected={step.feeEnvelope.expectedFeeMicros} worst={step.feeEnvelope.worstCaseFeeMicros}
                </div>
                <div>drivers: {step.feeEnvelope.dominantCostDrivers.join(', ')}</div>
                <div>
                  receipt: status={step.receipt.status} reason={step.receipt.reasonCode} oracle={step.receipt.oracleCheck} risk={step.receipt.riskCheck} batch={step.receipt.batchSummary}
                </div>
                {step.riskSnapshot && (
                  <div>
                    risk-snapshot: equity={step.riskSnapshot.equity} collateral_ratio={step.riskSnapshot.collateralRatioBps}bps init={step.riskSnapshot.initMarginRatioBps}bps maint={step.riskSnapshot.maintMarginRatioBps}bps
                  </div>
                )}
                <div>events: {step.eventPreview.join(', ') || 'none'}</div>
                {step.constraintTrace.length > 0 && (
                  <div>
                    {step.constraintTrace.map((trace, idx) => (
                      <div key={idx}>
                        [{trace.category}] {trace.check} | observed={trace.observed} expected={trace.expected} result={trace.passed ? 'pass' : 'fail'}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {marketReport.rejects.length > 0 && (
              <div className={styles.errorDetail}>
                <strong>Rejection Visibility</strong>
                {marketReport.rejects.map((r, i) => <div key={i}>{r}</div>)}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
